import {
  Autocomplete,
  Button,
  Flex,
  Grid,
  Label,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import { LayoutContext } from "layout/context/LayoutContext";
import { listCompanies } from "graphql/queries";
import TechnicianCard from "./TechnicianCard";

const ToolForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const [toolForm, setToolForm] = useState(initialValues);
  const [companies, setCompanies] = useState({
    list: [],
    loading: false,
  });
  const [errors, setErrors] = useState({
    name: "",
    companyID: "",
  });
  const validateErrors = useErrors({
    name: "isRequired",
    companyID: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(toolForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (checkIncorrectValue("companyID", companies.list)) return;

    if (!hasErrors) onSubmit(toolForm);
  };

  const checkIncorrectValue = (name, list) => {
    if (!list.some((rec) => rec.id === toolForm[name])) {
      setErrors((prevSt) => ({
        ...prevSt,
        [name]: strings.validations.incorrectValue,
      }));
      return true;
    }
    return false;
  };

  const selectTechnician = (technicianID) => {
    setToolForm((prevSt) => ({
      ...prevSt,
      technicianID,
    }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setToolForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    if (name === "companyID") {
      fetchCompanies({ name: value });
    }
    setToolForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    setToolForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onClearSelect = (name) => {
    const value = "";
    if (name === "companyID") {
      fetchCompanies({ name: value });
    }
    setToolForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({
      name: "",
      companyID: "",
    });
    setToolForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompanies({ list: [], loading: true });

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies((prev) => ({ ...prev, list: companiesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setCompanies((prev) => ({ ...prev, loading: false }));
    }
  }

  useEffect(() => {
    setToolForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <TextField
            name="name"
            label={<LabelRequired text={strings.fields.name.label} />}
            value={toolForm.name}
            onChange={onChange}
            errorMessage={errors.name}
            hasError={!!errors.name}
          ></TextField>
          <TextField
            name="location"
            label={strings.fields.location.label}
            value={toolForm.location}
            onChange={onChange}
          ></TextField>
        </Grid>
        <TextAreaField
          name="description"
          label={strings.fields.description.label}
          value={toolForm.description}
          onChange={onChange}
        ></TextAreaField>
        <Grid
          templateColumns={{ base: "1fr", large: "2fr 2fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          {type === "create" && (
            <Flex direction="column" gap="xs">
              <Label htmlFor="companyID">
                <LabelRequired text={strings.fields.company.label} />
              </Label>
              <Autocomplete
                id="companyID"
                name="companyID"
                isRequired
                placeholder={strings.fields.company.placeholder}
                value={
                  companies.list.find(
                    (companyRec) => companyRec.id === toolForm.companyID
                  )?.name ?? toolForm.companyID
                }
                options={companies.list.map((companyRec) => ({
                  id: companyRec.id,
                  label: companyRec.name,
                }))}
                isLoading={companies.loading}
                onClear={() => onClearSelect("companyID")}
                onChange={onChangeSelect}
                onSelect={({ id }) => onSelect("companyID", id)}
                errorMessage={errors.companyID}
                hasError={!!errors.companyID}
                menuSlots={{ Empty: globalStrings.noResults }}
              />
            </Flex>
          )}
        </Grid>
        <Grid
          templateColumns={{ base: "1fr", large: "1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <TechnicianCard
            technicianID={toolForm.technicianID}
            selectTechnician={selectTechnician}
          />
        </Grid>
        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          ></Button>
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          ></Button>
        </Flex>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: {
        label: "Nombre",
      },
      location: {
        label: "Ubicación",
      },
      description: {
        label: "Descripción",
      },
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
    },
  },
});

export default ToolForm;
